export type EnterpriseDealStatus = 'draft' | 'active' | 'expired' | 'cancelled';
export type EnterprisePaymentStatus = 'pending' | 'confirmed' | 'waived';

export interface EnterpriseDealLifecycleLike {
  status: EnterpriseDealStatus | string;
  payment_status?: EnterprisePaymentStatus | string | null;
  contract_start?: string | null;
  contract_end?: string | null;
  grace_period_days?: number | null;
  plan_id?: string | null;
}

export interface EnterpriseServiceDraft {
  service_id: string;
  service_key: string;
  service_name: string;
  is_enabled: boolean;
  usage_limit: string;
  limit_type: 'monthly' | 'active' | 'total' | 'unlimited';
  base_shortlist_pass_limit: string;
  max_replacement_pass_limit: string;
}

export function calculateEnterpriseContractTotals(subtotalInput: string | number, vatRateInput: string | number) {
  const subtotal = Math.max(0, Number(subtotalInput) || 0);
  const vatRate = Math.min(100, Math.max(0, Number(vatRateInput) || 0));
  const vat = Math.round((subtotal * vatRate / 100) * 100) / 100;
  return {
    subtotal: Math.round(subtotal * 100) / 100,
    vatRate,
    vat,
    total: Math.round((subtotal + vat) * 100) / 100,
  };
}

function contractExpiry(deal: EnterpriseDealLifecycleLike) {
  if (!deal.contract_end) return null;
  const end = new Date(`${deal.contract_end.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(end.getTime())) return null;
  end.setUTCDate(end.getUTCDate() + 1 + Math.max(0, deal.grace_period_days ?? 0));
  return end;
}

export function effectiveEnterpriseDealStatus(
  deal: EnterpriseDealLifecycleLike,
  now = new Date(),
): EnterpriseDealStatus | string {
  if (deal.status !== 'active') return deal.status;
  const expiry = contractExpiry(deal);
  return expiry && expiry <= now ? 'expired' : 'active';
}

export function enterpriseDealActivationBlocker(
  deal: EnterpriseDealLifecycleLike,
  now = new Date(),
) {
  if (deal.status !== 'draft') return 'Only a draft deal can be activated.';
  if (!['confirmed', 'waived'].includes(deal.payment_status ?? '')) {
    return 'Confirm the bank transfer before activation.';
  }
  if (!deal.plan_id) return 'Save the configured services before activation.';
  if (!deal.contract_start || !deal.contract_end) return 'Contract dates are required.';

  const today = now.toISOString().slice(0, 10);
  if (today < deal.contract_start.slice(0, 10)) return 'The contract start date has not arrived.';
  if (today > deal.contract_end.slice(0, 10)) return 'The contract has already ended.';
  return null;
}

export function validateEnterpriseServices(services: EnterpriseServiceDraft[]) {
  const enabled = services.filter((service) => service.is_enabled);
  if (enabled.length === 0) return 'Select at least one service.';

  for (const service of enabled) {
    if (service.service_key === 'candidate_pipeline') {
      if (Number(service.base_shortlist_pass_limit) <= 0 || Number(service.max_replacement_pass_limit) < 0) {
        return `Configure both candidate pipeline limits for ${service.service_name}.`;
      }
      continue;
    }
    if (service.limit_type !== 'unlimited' && (!service.usage_limit || Number(service.usage_limit) < 0)) {
      return `Enter a valid allowance for ${service.service_name}.`;
    }
  }
  return null;
}

export function serializeEnterpriseServices(services: EnterpriseServiceDraft[]) {
  return services.map((service) => ({
    service_id: service.service_id,
    is_enabled: service.is_enabled,
    usage_limit: service.limit_type === 'unlimited' || service.service_key === 'candidate_pipeline'
      ? null
      : Number(service.usage_limit),
    limit_type: service.service_key === 'candidate_pipeline' ? 'total' : service.limit_type,
    ...(service.service_key === 'candidate_pipeline'
      ? {
          base_shortlist_pass_limit: Number(service.base_shortlist_pass_limit),
          max_replacement_pass_limit: Number(service.max_replacement_pass_limit),
        }
      : {}),
  }));
}
